import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Users, Target, Activity, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { usePageSEO } from "@/lib/seo";
import { PROGRAM_BY_KEY, PROJECT_STATUS_LABEL, PROJECT_STATUS_COLOR } from "@/lib/programs";

function renderParagraphs(text) {
  if (!text) return null;
  return text.split(/\n{2,}/).map((chunk, i) => {
    // Bullet list detection
    if (chunk.trim().split("\n").every((l) => l.trim().startsWith("• "))) {
      return (
        <ul key={i} className="list-none space-y-2 my-2">
          {chunk.trim().split("\n").map((l, j) => (
            <li key={j} className="flex items-start gap-3 text-[16.5px] text-inkMuted leading-relaxed">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold mt-2.5 shrink-0" />
              <span>{l.replace(/^•\s*/, "")}</span>
            </li>
          ))}
        </ul>
      );
    }
    const parts = chunk.split(/(\*\*[^*]+\*\*)/g).map((p, j) => {
      if (/^\*\*[^*]+\*\*$/.test(p))
        return (
          <strong key={j} className="text-petrol font-semibold">
            {p.replace(/\*\*/g, "")}
          </strong>
        );
      // Preserve single line breaks inside paragraph
      return (
        <span key={j}>
          {p.split("\n").map((line, k, arr) => (
            <span key={k}>
              {line}
              {k < arr.length - 1 && <br />}
            </span>
          ))}
        </span>
      );
    });
    return (
      <p key={i} className="text-[17px] leading-[1.75] text-inkMuted text-pretty">
        {parts}
      </p>
    );
  });
}

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  usePageSEO({
    title: project ? `${project.title} | Stichting FIN` : "Project | Stichting FIN",
    description: project?.description_short,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/projects/${slug}`)
      .then((r) => !cancelled && setProject(r.data))
      .catch((e) => !cancelled && setError(e.response?.status === 404 ? "Project niet gevonden" : e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading)
    return (
      <div className="container-x py-24 text-center text-inkMuted" data-testid="project-detail-loading">
        Project laden…
      </div>
    );

  if (error || !project)
    return (
      <div className="container-x py-24 text-center max-w-xl mx-auto" data-testid="project-detail-error">
        <p className="font-display text-3xl text-petrol mb-2">Project niet gevonden</p>
        <p className="text-inkMuted mb-6">Het project dat u zoekt bestaat niet (meer). Bekijk het overzicht.</p>
        <Link
          to="/projecten"
          className="inline-flex items-center gap-2 bg-petrol text-cream rounded-full px-5 py-2.5 text-sm font-semibold"
        >
          Alle projecten
        </Link>
      </div>
    );

  const prog = PROGRAM_BY_KEY[project.theme];

  return (
    <div data-testid="project-detail-page">
      <section className="relative bg-petrol text-cream overflow-hidden">
        <div aria-hidden="true" className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-gold/15 blur-3xl" />
        <div className="container-x pt-14 md:pt-20 pb-14 relative">
          <Link
            to="/projecten"
            className="inline-flex items-center gap-1.5 text-cream/80 font-semibold text-sm hover:text-gold transition mb-6"
            data-testid="project-back-link"
          >
            <ArrowLeft size={15} /> Terug naar projecten
          </Link>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {prog && (
              <span className="inline-flex items-center gap-2 bg-cream/10 border border-cream/20 rounded-full px-3.5 py-1.5">
                <span className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-gold">{prog.label}</span>
              </span>
            )}
            <span className={`text-[11px] uppercase tracking-[0.22em] font-bold ${PROJECT_STATUS_COLOR[project.status] || "text-gold"} bg-cream/10 rounded-full px-3 py-1`}>
              {PROJECT_STATUS_LABEL[project.status] || project.status}
            </span>
            {project.locatie && (
              <span className="inline-flex items-center gap-1 text-cream/70 text-[13px]">
                <MapPin size={13} /> {project.locatie}
              </span>
            )}
          </div>
          <h1 className="font-display text-[clamp(2.2rem,5.5vw,4.2rem)] leading-[1.05] tracking-tight text-balance max-w-4xl">
            {project.title}
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-cream/85 max-w-3xl text-pretty">
            {project.description_short}
          </p>
        </div>
      </section>

      <section className="py-14 md:py-16 bg-cream">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <article className="lg:col-span-8 space-y-5">
            {project.image_url && (
              <div className="rounded-3xl overflow-hidden shadow-soft mb-8">
                <img src={project.image_url} alt={project.title} className="w-full h-auto object-cover" />
              </div>
            )}
            {renderParagraphs(project.description_long)}
          </article>

          <aside className="lg:col-span-4 space-y-4">
            <div className="bg-sandSoft border border-line rounded-3xl p-6 shadow-soft">
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-gold mb-4">Projectinfo</p>
              <ul className="space-y-4 text-sm">
                {prog && (
                  <li className="flex items-start gap-3">
                    <Target size={16} className="text-gold mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-inkMuted mb-0.5">Programma</p>
                      <p className="text-petrol font-semibold">{prog.label}</p>
                    </div>
                  </li>
                )}
                <li className="flex items-start gap-3">
                  <Activity size={16} className="text-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-inkMuted mb-0.5">Status</p>
                    <p className="text-petrol font-semibold">{PROJECT_STATUS_LABEL[project.status] || project.status}</p>
                  </div>
                </li>
                {project.locatie && (
                  <li className="flex items-start gap-3">
                    <MapPin size={16} className="text-gold mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-inkMuted mb-0.5">Locatie</p>
                      <p className="text-petrol font-semibold">{project.locatie}</p>
                    </div>
                  </li>
                )}
                {project.doelgroep && (
                  <li className="flex items-start gap-3">
                    <Users size={16} className="text-gold mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-inkMuted mb-0.5">Voor wie</p>
                      <p className="text-petrol text-[14px] leading-relaxed">{project.doelgroep}</p>
                    </div>
                  </li>
                )}
              </ul>
            </div>

            {project.resultaten && (
              <div className="bg-goldSoft border border-line rounded-3xl p-6">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-goldDark mb-3">Wat wij meten</p>
                <p className="text-petrol text-[14.5px] leading-relaxed">{project.resultaten}</p>
              </div>
            )}

            <div className="bg-petrol text-cream rounded-3xl p-6 relative overflow-hidden">
              <div aria-hidden="true" className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gold/25 blur-3xl" />
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-gold mb-3 relative">Meer weten?</p>
              <p className="text-cream/85 text-[14.5px] leading-relaxed mb-5 relative">
                Neem contact op als u meer wilt weten of wilt onderzoeken of dit project bij uw situatie past.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-gold text-petrol hover:bg-goldSoft transition rounded-full px-5 py-2.5 text-sm font-semibold relative"
                data-testid="project-cta-contact"
              >
                Neem contact op <ArrowUpRight size={15} />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
